export const SITE_NAME = "JOBUWIN";
export const DEFAULT_SITE_TITLE = "Genuine jobs. Real people.";
export const DEFAULT_SITE_DESCRIPTION =
  "JOBUWIN connects genuine job opportunities with real people — a hiring platform designed to reduce fake profiles, AI-generated noise, and meaningless applications.";

export const DEFAULT_OG_IMAGE_PATH = "/og/jobuwin-share.png";

/** Public marketing + content URLs we want search and AI crawlers to discover. */
export const PUBLIC_INDEXABLE_PATHS = [
  { path: "/", label: "Home" },
  { path: "/job-seekers", label: "For job seekers" },
  { path: "/job-search", label: "Find jobs" },
  { path: "/hiring-managers", label: "For hiring managers" },
  { path: "/pricing", label: "Pricing" },
  { path: "/blog", label: "Blog" },
  { path: "/blog/introducing-jobuwin", label: "Introducing JOBUWIN" },
  { path: "/blog/hiring-is-broken", label: "Hiring is broken" },
  { path: "/blog/applying-for-jobs-is-broken", label: "Applying for jobs is broken" },
  { path: "/terms", label: "Terms of Use" },
  { path: "/privacy", label: "Privacy Policy" },
  { path: "/cookies", label: "Cookie Policy" },
];

export function getPublicSiteOrigin() {
  const fromEnv = String(process.env.REACT_APP_PUBLIC_SITE_URL || "").trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://www.jobuwin.com";
}

export function buildPublicPageUrl(path = "/") {
  const origin = getPublicSiteOrigin();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${normalizedPath}`;
}

export function buildPageTitle(title, { includeSiteName = true } = {}) {
  const trimmed = String(title || "").trim();
  if (!trimmed) return SITE_NAME;
  if (!includeSiteName || trimmed.includes(SITE_NAME)) return trimmed;
  return `${trimmed} | ${SITE_NAME}`;
}

export function truncateMetaDescription(text, maxLength = 160) {
  const normalized = String(text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    legalName: "MotoMob Limited",
    url: buildPublicPageUrl("/"),
    logo: buildPublicPageUrl("/jobuwin-icon.svg"),
    email: "hello@jobuwin.com",
    sameAs: ["https://www.linkedin.com/company/jobuwin", "https://www.youtube.com/@JOBUWIN"],
  };
}

export function buildWebSiteJsonLd(description) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: buildPublicPageUrl("/"),
    description: truncateMetaDescription(description || DEFAULT_SITE_DESCRIPTION),
    inLanguage: "en-GB",
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: buildPublicPageUrl("/"),
    },
  };
}

export function buildWebPageJsonLd({ title, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: buildPageTitle(title, { includeSiteName: false }),
    description: truncateMetaDescription(description),
    url,
    inLanguage: "en-GB",
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: buildPublicPageUrl("/"),
    },
  };
}

export function buildBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Build breadcrumb nav items for visible UI + JSON-LD helpers.
 * @param {{ name: string, path?: string, current?: boolean }[]} trail
 */
export function buildBreadcrumbNavItems(trail) {
  return (trail || []).map((item, index) => {
    const path = String(item.path || "").trim();
    const isLast = index === trail.length - 1;
    const current = Boolean(item.current) || isLast;
    return {
      name: String(item.name || "").trim() || "Page",
      path: path || "/",
      url: buildPublicPageUrl(path || "/"),
      current,
    };
  });
}

/** Paths that should never be indexed (authenticated workspaces, dev tools, archives). */
const PRIVATE_ROUTE_PREFIXES = [
  "/applicant-home",
  "/applicant-profile",
  "/applicant/",
  "/hiring-dashboard",
  "/recruitment-profile",
  "/purchases",
  "/response-settings",
  "/hiring/",
  "/notifications",
  "/design",
  "/old1/",
  "/nd1/",
];

export function isPrivateAppRoute(pathname = "") {
  const path = String(pathname || "");
  return PRIVATE_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
}
